import type { ImportNodeInput } from '@/api/generatedApi';
import xlsx from 'node-xlsx';

export interface CellError {
  row: number;
  column: number;
  value: string;
  message: string;
}

export class TaxonomyImportValidationError extends Error {
  readonly errors: CellError[];

  constructor(message: string, errors: CellError[]) {
    super(message);
    this.name = 'TaxonomyImportValidationError';
    this.errors = errors;
  }
}

export interface ParsedTaxonomyImport {
  tree: ImportNodeInput[];
  totalNodeCount: number;
  maxDepth: number;
}

export const IMPORT_ERRORS = {
  FILE_SIZE_EXCEEDED: 'File size exceeds maximum limit of 10MB.',
  UNSUPPORTED_FORMAT: 'Unsupported file format. Please use CSV, XLSX, or XLS files.',
  NO_WORKSHEETS: 'No worksheets found in the file.',
  INSUFFICIENT_DATA: 'File must contain at least a header row and one data row.',
  INVALID_HEADERS:
    'Headers must follow the pattern "Level 1", "Level 2", "Level 3", etc. in sequential order starting at 1.',
  MAX_DEPTH_EXCEEDED: (depth: number) => `Import exceeds maximum depth of 10 levels (found ${depth}).`,
  MAX_NODES_EXCEEDED: (count: number) => `Import exceeds maximum of 5,000 nodes (found ${count}).`,
  PARSE_FAILED: 'Failed to parse file. Please ensure it is a valid spreadsheet file.',
} as const;

const MAX_FILE_SIZE = 10 * 1024 * 1024;
const MAX_DEPTH = 10;
const MAX_NODES = 5000;
const LEVEL_HEADER_PATTERN = /^Level (\d+)$/i;

function validateHeaders(headers: string[]): number {
  if (headers.length === 0) {
    throw new Error(IMPORT_ERRORS.INVALID_HEADERS);
  }

  for (const [index, header] of headers.entries()) {
    const match = LEVEL_HEADER_PATTERN.exec(header?.trim() ?? '');
    if (!match || Number.parseInt(match[1], 10) !== index + 1) {
      throw new Error(IMPORT_ERRORS.INVALID_HEADERS);
    }
  }

  return headers.length;
}

function extractPath(row: string[], levelCount: number, rowNumber: number, errors: CellError[]): string[] {
  const path: string[] = [];
  let foundEmptyAt = -1;

  for (let column = 0; column < levelCount; column++) {
    const value = row[column]?.toString().trim() ?? '';

    if (value === '') {
      if (foundEmptyAt === -1) {
        foundEmptyAt = column;
      }
    } else {
      if (foundEmptyAt !== -1) {
        errors.push({
          row: rowNumber,
          column: column + 1,
          value,
          message: `Gap in path: Level ${foundEmptyAt + 1} is empty but Level ${column + 1} has a value.`,
        });
        return [];
      }
      if (value.length > 255) {
        errors.push({
          row: rowNumber,
          column: column + 1,
          value: value.slice(0, 50) + '...',
          message: `Name exceeds 255 characters (${value.length} characters).`,
        });
        return [];
      }
      path.push(value);
    }
  }

  return path;
}

interface TreeBuildNode {
  name: string;
  children: Map<string, TreeBuildNode>;
}

function insertPath(roots: Map<string, TreeBuildNode>, path: string[]): void {
  let currentLevel = roots;

  for (const name of path) {
    const key = name.toLowerCase();
    let node = currentLevel.get(key);
    if (!node) {
      node = { name, children: new Map() };
      currentLevel.set(key, node);
    }
    currentLevel = node.children;
  }
}

function convertToImportNodes(nodes: Map<string, TreeBuildNode>): ImportNodeInput[] {
  return [...nodes.values()].map(node => ({
    name: node.name,
    children: convertToImportNodes(node.children),
  }));
}

function countNodes(nodes: ImportNodeInput[]): number {
  let count = 0;
  for (const node of nodes) {
    count += 1 + countNodes(node.children);
  }
  return count;
}

function getMaxDepth(nodes: ImportNodeInput[], depth = 1): number {
  let max = depth;
  for (const node of nodes) {
    if (node.children.length > 0) {
      max = Math.max(max, getMaxDepth(node.children, depth + 1));
    }
  }
  return max;
}

function findDuplicateNames(dataRows: string[][], levelCount: number): CellError[] {
  const errors: CellError[] = [];
  const reportedNames = new Set<string>();
  const nameLocations = new Map<string, { row: number; column: number; parentPath: string }>();

  for (const [index, row] of dataRows.entries()) {
    const rowNumber = index + 2;
    const pathSoFar: string[] = [];

    for (let column = 0; column < levelCount; column++) {
      const value = row[column]?.toString().trim() ?? '';
      if (value === '') break;

      const lower = value.toLowerCase();
      const parentPath = pathSoFar.join('/').toLowerCase();
      const locationKey = `${lower}@${parentPath}`;
      const existing = nameLocations.get(lower);

      if (existing) {
        const existingLocationKey = `${lower}@${existing.parentPath}`;
        if (existingLocationKey !== locationKey && !reportedNames.has(lower)) {
          reportedNames.add(lower);
          errors.push({
            row: rowNumber,
            column: column + 1,
            value,
            message: `Duplicate name "${value}" — also appears at row ${existing.row}, Level ${existing.column}. All names must be unique across the entire taxonomy.`,
          });
        }
      } else {
        nameLocations.set(lower, { row: rowNumber, column: column + 1, parentPath });
      }

      pathSoFar.push(lower);
    }
  }

  return errors;
}

function parseCsvText(text: string): string[][] {
  const rows: string[][] = [];
  let current = '';
  let inQuotes = false;
  let row: string[] = [];

  for (let index = 0; index < text.length; index++) {
    const char = text[index];

    if (inQuotes) {
      if (char === '"') {
        if (index + 1 < text.length && text[index + 1] === '"') {
          current += '"';
          index++;
        } else {
          inQuotes = false;
        }
      } else {
        current += char;
      }
    } else if (char === '"') {
      inQuotes = true;
    } else if (char === ',') {
      row.push(current);
      current = '';
    } else if (char === '\n' || (char === '\r' && text[index + 1] === '\n')) {
      row.push(current);
      current = '';
      if (row.some(cell => cell.trim() !== '')) {
        rows.push(row);
      }
      row = [];
      if (char === '\r') index++;
    } else if (char === '\r') {
      row.push(current);
      current = '';
      if (row.some(cell => cell.trim() !== '')) {
        rows.push(row);
      }
      row = [];
    } else {
      current += char;
    }
  }

  row.push(current);
  if (row.some(cell => cell.trim() !== '')) {
    rows.push(row);
  }

  return rows;
}

export async function parseTaxonomyImportFile(file: File): Promise<ParsedTaxonomyImport> {
  if (file.size > MAX_FILE_SIZE) {
    throw new Error(IMPORT_ERRORS.FILE_SIZE_EXCEEDED);
  }

  const fileExtension = file.name.toLowerCase().split('.').pop();
  if (!fileExtension || !['csv', 'xlsx', 'xls'].includes(fileExtension)) {
    throw new Error(IMPORT_ERRORS.UNSUPPORTED_FORMAT);
  }

  try {
    let jsonData: string[][];

    const arrayBuffer = await file.arrayBuffer();

    if (fileExtension === 'csv') {
      const text = new TextDecoder('utf8').decode(arrayBuffer).replace(/^\uFEFF/, '');
      jsonData = parseCsvText(text);
    } else {
      const worksheets = xlsx.parse(arrayBuffer);

      if (!worksheets || worksheets.length === 0) {
        throw new Error(IMPORT_ERRORS.NO_WORKSHEETS);
      }

      const firstWorksheet = worksheets[0];
      jsonData = firstWorksheet.data as string[][];
    }

    if (jsonData.length < 2) {
      throw new Error(IMPORT_ERRORS.INSUFFICIENT_DATA);
    }

    const headers = jsonData[0];
    const dataRows = jsonData.slice(1);

    const levelCount = validateHeaders(headers);

    if (levelCount > MAX_DEPTH) {
      throw new Error(IMPORT_ERRORS.MAX_DEPTH_EXCEEDED(levelCount));
    }

    const cellErrors: CellError[] = [];
    const roots: Map<string, TreeBuildNode> = new Map();

    for (const [index, row] of dataRows.entries()) {
      const rowNumber = index + 2;
      const path = extractPath(row, levelCount, rowNumber, cellErrors);

      if (path.length === 0) {
        continue;
      }

      insertPath(roots, path);
    }

    const duplicateErrors = findDuplicateNames(dataRows, levelCount);
    cellErrors.push(...duplicateErrors);

    if (cellErrors.length > 0) {
      const count = cellErrors.length;
      throw new TaxonomyImportValidationError(
        `Found ${count} validation ${count === 1 ? 'error' : 'errors'} in the file.`,
        cellErrors
      );
    }

    const tree = convertToImportNodes(roots);
    const totalNodeCount = countNodes(tree);
    const maxDepth = tree.length > 0 ? getMaxDepth(tree) : 0;

    if (totalNodeCount === 0) {
      throw new Error(IMPORT_ERRORS.INSUFFICIENT_DATA);
    }

    if (totalNodeCount > MAX_NODES) {
      throw new Error(IMPORT_ERRORS.MAX_NODES_EXCEEDED(totalNodeCount));
    }

    if (maxDepth > MAX_DEPTH) {
      throw new Error(IMPORT_ERRORS.MAX_DEPTH_EXCEEDED(maxDepth));
    }

    return { tree, totalNodeCount, maxDepth };
  } catch (error) {
    if (error instanceof Error) {
      throw error;
    }
    throw new Error(IMPORT_ERRORS.PARSE_FAILED);
  }
}
